/**
 * The engagement-basis selector and its consequence panel — Step 1.
 *
 * Split out of `ClassifyStep` under the 350-line rule in
 * `docs/FILE_SIZE_POLICY.md` (ATTEST-UX-2 Bundle 2). Presentation only: this
 * component decides nothing. The basis it reports is a *declaration* the server
 * re-reads off `engagement_basis` on the request, and the engagement type is
 * taken from the option the registry published rather than derived here.
 *
 * The panel below the select exists because the basis is the one choice on this
 * screen that changes what the letter may say. A selector with no visible
 * consequence is a choice made blind, and a CPA who cannot see what they picked
 * cannot notice they picked wrong.
 */
import type { EngagementBasisChoice } from '@/components/attest/api'
import { AT_C_215_AUP } from '@/components/attest/classifyQuestions'
import { HelpDot } from '@/components/attest/wizardHelp'
import { Label } from '@/components/ui/label'

export interface EngagementBasisPickerProps {
  choice: EngagementBasisChoice
  /** The resolved key — the CPA's pick, or the choice's own default. */
  basisKey: string
  onSelectBasis: (key: string) => void
}

export function EngagementBasisPicker({
  choice, basisKey, onSelectBasis,
}: EngagementBasisPickerProps) {
  const basis = choice.options.find(o => o.key === basisKey) ?? null

  return (
    <div data-testid="engagement-basis-block">
      <div className="flex items-center gap-1">
        <Label htmlFor="attest-engagement-basis">Engagement basis</Label>
        <HelpDot field="engagementBasis" />
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        The same ask can be answered on more than one lawful basis. The basis
        decides what the letter may <em>say</em>. It does not decide what the
        letter refuses — the deny list is the same list under either one.
      </p>
      <select
        id="attest-engagement-basis"
        data-testid="engagement-basis-select"
        className="mt-2 h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
        value={basisKey}
        onChange={e => onSelectBasis(e.target.value)}
      >
        {choice.options.map(o => (
          <option key={o.key} value={o.key}>
            {o.label}
            {o.key === choice.default ? ' (default)' : ''}
          </option>
        ))}
      </select>
      {basis && (
        <div
          data-testid="engagement-basis-detail"
          data-engagement-type={basis.engagement_type}
          className="mt-2 rounded border border-border bg-muted/40 p-3"
        >
          <p className="text-xs text-gray-700">{basis.description}</p>
          <p className="mt-2 text-xs text-gray-700">
            <span className="font-semibold">Independence: </span>
            {basis.independence_note}
          </p>
          {/* The retired yes/no, read-only (ATTEST-BASIS-UNIFY-1). Stating
              the consequence is the useful half of that question; asking for
              it was the half that let the answer contradict the basis. */}
          <p className="mt-2 text-xs text-gray-700" data-testid="basis-derived-scope">
            <span className="font-semibold">Work beyond your own records: </span>
            {basis.engagement_type === AT_C_215_AUP
              ? 'yes — this basis is agreed-upon procedures, so the letter reports '
                + 'findings from work on records this firm did not produce.'
              : 'no — this basis speaks only from this firm’s own files. If the ask '
                + 'needs more than they show, change the basis above rather than the letter.'}
          </p>
          <p className="mt-2 font-mono text-[11px] text-muted-foreground">
            {basis.engagement_type} · independence{' '}
            {basis.independence_required ? 'required' : 'disclosed, not required'}
            {' · '}
            {basis.denied_assertions.length} assertions refused under either basis
          </p>
        </div>
      )}
    </div>
  )
}
